import React from 'react';
import { Home, AlertTriangle, CheckCircle2, Info } from 'lucide-react';

const BAND_STYLE = {
  green: 'bg-green-50 border-green-400 text-green-800',
  yellow: 'bg-yellow-50 border-yellow-400 text-yellow-800',
  orange: 'bg-orange-50 border-orange-400 text-orange-800',
  red: 'bg-red-50 border-red-400 text-red-800',
  neutral: 'bg-secondary/50 border-border text-primary',
};

function Metric({ label, value }) {
  return (
    <div className="bg-background border border-border rounded-2xl p-4">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <p className="text-xl font-bold text-primary mt-1">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Струп. Принцип: сервер присылает ЧИСЛА и КОДЫ ФЛАГОВ (score_result.stroop,
// stroop-web-4), здесь только подача. Вердиктов «норма/нарушение» нет — у
// цифровой голосовой версии нет нормативов; нормы подключаются на сервере
// (NORMS в stroop.py), и тогда здесь появляются z-оценки.
// ---------------------------------------------------------------------------

const CARD_TITLES = ['A — слова (чтение)', 'B — цвета (квадраты)', 'C — цвет-слово'];
const CARD_LETTERS = ['A', 'B', 'C'];

const LEVEL_STYLE = {
  ok: { band: 'green', dot: 'bg-green-500', label: 'Запись хорошая' },
  warn: { band: 'yellow', dot: 'bg-yellow-500', label: 'Есть замечания' },
  bad: { band: 'orange', dot: 'bg-orange-500', label: 'Данные ненадёжны' },
  no_reference: { band: 'yellow', dot: 'bg-gray-400', label: 'Нет данных' },
};

const VALIDITY = {
  valid: { band: 'green', Icon: CheckCircle2, text: 'Запись пригодна для анализа.' },
  limited: { band: 'yellow', Icon: AlertTriangle, text: 'Запись пригодна с ограничениями — см. «Качество записи» и «Наблюдения».' },
  invalid: { band: 'orange', Icon: AlertTriangle, text: 'Производные показатели не рассчитаны: запись непригодна для анализа.' },
};

const ENDED_TEXT = {
  auto: 'дошёл до конца',
  idle: 'закрыта по тишине',
  manual: 'закрыта вручную',
};

const SOURCE_TEXT = { interim: 'речь', wall: 'время карточки', manual: 'справочно' };

const CARD_FLAG_TEXT = {
  card_manual: 'закрыта вручную — время справочно',
  card_not_finished: 'распознано менее 80% карточки (не дочитал или распознавание оборвалось) — время не используется',
  card_partial: 'последние слова не распознаны: пациент мог не дочитать, либо распознавание их не зафиксировало',
  asr_loss_warn: 'часть слов не дошла до распознавания',
  asr_loss_high: 'много слов не дошло до распознавания',
};

const PILOT_LABELS = {
  colorVision: ['Цветовое зрение', { normal: 'норма', impaired: 'нарушено', unknown: 'не проверялось' }],
  speech: ['Речь', { normal: 'норма', dysarthria: 'дизартрия', aphasia: 'афазия', unknown: 'не оценивалась' }],
  corrected: ['Зрение скорректировано', { yes: 'да / не нужно', no: 'нет', unknown: 'не выяснялось' }],
  fatigue: ['Утомление / сонливость', { yes: 'есть', no: 'нет', unknown: 'не выяснялось' }],
  sedatives: ['Седативные за сутки', { yes: 'да', no: 'нет', unknown: 'не выяснялось' }],
};

const num = (v, d = 1) => (v === null || v === undefined || Number.isNaN(v) ? '—' : Number(v).toFixed(d));
const signed = (v, d = 1) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(d)}`);

function Card({ title, children, note }) {
  return (
    <div className="bg-secondary/50 rounded-3xl p-6 shadow-xl border border-border">
      {title && <h2 className="text-xl font-semibold text-primary mb-3">{title}</h2>}
      {children}
      {note && <p className="text-[10px] text-muted-foreground/70 mt-3 italic">{note}</p>}
    </div>
  );
}

function Tile({ label, value, sub, big }) {
  return (
    <div className="bg-background border border-border rounded-2xl p-4">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <p className={`${big ? 'text-3xl text-accent' : 'text-xl text-primary'} font-bold mt-1`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

// Почему производные показатели не посчитаны — по кодам флагов сервера
function unavailableReasons(s) {
  const flags = s.flags || [];
  const out = [];
  if (flags.includes('basis_none')) out.push('Одна из карточек закрыта вручную: её время справочное, показатели по времени не считаются.');
  CARD_LETTERS.forEach((l) => {
    if (flags.includes(`${l}:card_not_finished`)) out.push(`Карточка ${l}: пациент дошёл менее чем до 80% — время не используется.`);
  });
  if (out.length === 0) out.push('Недостаточно данных для расчёта.');
  return out;
}

function observations(s) {
  const flags = new Set(s.flags || []);
  const per = s.per || [];
  const out = [];
  if (flags.has('c_faster_than_b')) out.push('Карточка C выполнена быстрее B (C/B меньше 1). Для интерференционной пробы это необычно: проверьте качество записи и ход выполнения.');
  if (flags.has('intrusions_on_c')) out.push(`На карточке C ${per[2]?.intrusions} раз(а) названо напечатанное слово вместо цвета чернил. Требует сопоставления с нормой.`);
  if (flags.has('possible_color_vision')) out.push('На карточке B есть путаница красного и зелёного. Возможна особенность цветового зрения: сопоставьте с отметкой «Цветовое зрение» и уточните при необходимости.');
  if (flags.has('perseveration_possible')) out.push('Есть серии из трёх и более одинаковых неверных ответов подряд (возможна персеверация).');
  if (flags.has('pilot_color_vision')) out.push('Отмечено нарушение цветового зрения — результат карточек B и C интерпретируется с учётом этого.');
  if (flags.has('pilot_speech')) out.push('Отмечены речевые нарушения (дизартрия/афазия) — распознавание и темп речи могут быть искажены.');
  if (flags.has('pilot_fatigue_sedation')) out.push('Отмечены утомление или приём седативных — возможное влияние на скорость.');
  if (flags.has('pilot_vision_uncorrected')) out.push('Зрение не скорректировано — возможное влияние на чтение карточек.');
  return out;
}

function QualityCard({ p, i }) {
  const st = LEVEL_STYLE[p.level] || LEVEL_STYLE.no_reference;
  return (
    <div className="bg-background border border-border rounded-2xl p-4">
      <div className="flex items-center gap-2">
        <span className={`inline-block size-3 rounded-full ${st.dot}`} />
        <p className="text-sm font-semibold text-primary">Карточка {CARD_TITLES[i]}</p>
      </div>
      <p className="text-xs text-muted-foreground mt-2">{st.label}</p>
      <ul className="text-xs mt-2 space-y-1">
        <li>Дошёл до: <span className="font-semibold">{p.reached ?? '—'}/{p.total}</span> слов</li>
        <li>Не дошло до распознавания: <span className="font-semibold">{num(p.lostShare, 1)}%</span></li>
        <li>Завершение: <span className="font-semibold">{ENDED_TEXT[p.endedBy] || p.endedBy}</span></li>
      </ul>
      {(p.flags || []).length > 0 && (
        <ul className="text-[11px] text-muted-foreground mt-2 list-disc pl-4 space-y-0.5">
          {p.flags.map(f => <li key={f}>{CARD_FLAG_TEXT[f] || f}</li>)}
        </ul>
      )}
    </div>
  );
}

function normNote(norms, key) {
  if (!norms) return 'норма: не задана';
  if (norms.status === 'set' && norms.z && norms.z[key] !== undefined) return `z = ${signed(norms.z[key], 2)} к норме (${norms.band})`;
  if (norms.status === 'no_age') return 'норма: нет возраста пациента';
  return 'норма: не задана';
}

function StroopBody({ r }) {
  const s = r.server ? r.raw : null;

  // Результат сервера не получен — показываем только это, ничего не считаем
  // на клиенте (клиентский расчёт расходился с сохраняемым).
  if (!s || !Array.isArray(s.per)) {
    return (
      <Card title="Результат не рассчитан">
        <p className="text-sm text-muted-foreground">
          Сервер не вернул расчёт по этому прохождению. Данные теста не потеряны, если отчёт был сохранён, откройте его из карточки пациента.
        </p>
      </Card>
    );
  }

  // Отчёт, сохранённый старой версией расчёта (до stroop-web-4): новых блоков нет
  if (!s.metrics) {
    return (
      <Card title="Показатели (расчёт прежней версии)" note="Отчёт сохранён до обновления расчёта: расширенные блоки недоступны.">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b-2 border-border text-muted-foreground text-xs">
              <th className="py-2 px-2">Субтест</th><th className="py-2 px-2">Время, с</th><th className="py-2 px-2">Верно</th>
              <th className="py-2 px-2">Ошибки</th><th className="py-2 px-2">Не услышано</th><th className="py-2 px-2">Лишние</th>
            </tr>
          </thead>
          <tbody>
            {s.per.map((p, i) => (p.total > 0 || p.heard > 0) && (
              <tr key={i} className="border-b border-border">
                <td className="py-2 px-2 text-primary font-medium">{CARD_TITLES[i]}</td>
                <td className="py-2 px-2">{p.span}</td><td className="py-2 px-2">{p.ok}</td>
                <td className="py-2 px-2">{p.sub}</td><td className="py-2 px-2">{p.del}</td><td className="py-2 px-2">{p.ins}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    );
  }

  const per = s.per;
  const m = s.metrics;
  const norms = s.norms;
  const validity = VALIDITY[s.validity] || VALIDITY.invalid;
  const vStyle = BAND_STYLE[validity.band];
  const metricsOk = m.ratioCB !== null && m.ratioCB !== undefined;
  const obs = observations(s);
  const shown = per.map((p, i) => [p, i]).filter(([p]) => p.total > 0 || p.heard > 0);

  const lat = per.map(p => p.latencyS);
  const latDiff = lat[1] != null && lat[2] != null ? Math.round((lat[2] - lat[1]) * 10) / 10 : null;
  const errorRows = shown.map(([p, i]) => ({ p, i }));
  const confusions = per.flatMap((p, i) => (p.confusions || []).slice(0, 3).map(c => ({ ...c, card: CARD_LETTERS[i] })));
  const runs = per.flatMap(p => p.runs || []);
  const pilot = s.pilot;

  return (
    <>
      {/* Сводка */}
      <div className={`rounded-3xl p-6 shadow-xl border-2 ${vStyle}`}>
        <div className="flex items-center gap-3">
          <validity.Icon className="size-6 shrink-0" />
          <p className="text-sm font-semibold">{validity.text}</p>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {per.map((p, i) => (
            <span key={i} className="text-[11px] bg-secondary text-primary px-2 py-1 rounded-full">
              {CARD_LETTERS[i]}: {p.perItemS != null ? `${num(p.perItemS, 2)} с/слово` : (p.perItemOwnS != null ? `${num(p.perItemOwnS, 2)} с/слово (справочно)` : 'время не используется')}
            </span>
          ))}
          <span className="text-[11px] bg-secondary text-primary px-2 py-1 rounded-full">C/B: {metricsOk ? `×${num(m.ratioCB, 2)}` : '—'}</span>
          <span className="text-[11px] bg-secondary text-primary px-2 py-1 rounded-full">Ошибок на C: {per[2]?.sub ?? '—'}</span>
          {norms && (norms.age != null || norms.educationYears != null) && (
            <span className="text-[11px] bg-secondary text-primary px-2 py-1 rounded-full">
              Возраст: {norms.age ?? '—'} · Образование, лет: {norms.educationYears ?? '—'}
            </span>
          )}
        </div>
      </div>

      {/* 1. Качество записи */}
      <Card title="Качество записи" note="Потери — слова, которые не дошли до распознавания: пропуск пациента и сбой распознавания без аудиозаписи неразличимы. Пороги светофора рабочие (до 5% — хорошо, 5–10% — замечания, больше 10% — ненадёжно).">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {shown.map(([p, i]) => <QualityCard key={i} p={p} i={i} />)}
        </div>
      </Card>

      {/* 2. Основные показатели */}
      <Card title="Основные показатели" note="Время на слово — основная мера: она не зависит от потерь распознавания. IG — вторичный расчётный индекс (правильных за 45 с), не норма Golden: другая длина карточек, 4 цвета, голосовой формат.">
        {metricsOk ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Tile big label="Замедление C/B" value={`×${num(m.ratioCB, 2)}`} sub={normNote(norms, 'ratioCB')} />
            <Tile label="C − B, с на слово" value={signed(m.diffCBItem, 2)} sub={normNote(norms, 'diffCBItem')} />
            <Tile label="B / A (названия цвета к чтению)" value={`×${num(m.ratioBA, 2)}`} sub="для сопоставления в динамике" />
            <Tile
              label="IG (расчётный)"
              value={m.ig == null ? '—' : signed(m.ig, 1)}
              sub={m.ig == null ? 'не рассчитан (мало распознанных слов)'
                : [m.igPlausible === false ? 'значение подозрительно большое — возможен артефакт распознавания' : null,
                  (s.flags || []).includes('ig_asr_sensitive') ? 'на значение влияют потери распознавания' : null,
                  normNote(norms, 'ig')].filter(Boolean).join(' · ')}
            />
          </div>
        ) : (
          <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
            {unavailableReasons(s).map(t => <li key={t}>{t}</li>)}
          </ul>
        )}
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-medium text-primary">Как читать эти показатели</summary>
          <div className="mt-2 space-y-2 text-xs text-muted-foreground">
            <p><span className="font-semibold text-primary">Замедление C/B.</span> Во сколько раз на одно слово карточка C (назвать цвет, которым напечатано слово, не читая само слово) медленнее карточки B (назвать цвет квадрата). Показывает, сколько стоит подавление автоматического чтения: чем больше число, тем сильнее замедление при конфликте. Не зависит от общего темпа речи пациента, поэтому это главный показатель.</p>
            <p><span className="font-semibold text-primary">C − B, с на слово.</span> Та же разница, но в секундах на слово. Удобна для сравнения у одного и того же пациента при повторном тестировании. Зависит от темпа речи, поэтому для сравнения разных пациентов менее надёжна, чем C/B.</p>
            <p><span className="font-semibold text-primary">B / A.</span> Во сколько раз называние цвета медленнее чтения слов (карточка A). Показывает фон: если это число тоже велико, замедление может быть общим (трудности называния, брадифрения), а не только следствием конфликта на карточке C. Читайте вместе с C/B.</p>
            <p><span className="font-semibold text-primary">IG (расчётный индекс).</span> Сколько слов в карточке C пациент назвал за 45 с больше или меньше, чем ожидалось по его скорости на A и B. Выше нуля: интерференция меньше ожидаемой; ниже нуля: больше. Это расчёт, а не норма Golden: он чувствителен к потерям распознавания (см. «Качество записи»), поэтому используйте его как вторичный ориентир.</p>
            <p>Для голосовой версии нормативы ещё собираются, поэтому подпись «норма: не задана» ожидаема. Пока нормы нет, ориентируйтесь на клиническую картину и на динамику у пациента, а не на пороговые значения.</p>
          </div>
        </details>
      </Card>

      {/* 3. Ошибки */}
      <Card title="Анализ ошибок">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b-2 border-border text-muted-foreground text-xs">
                <th className="py-2 px-2">Карточка</th>
                <th className="py-2 px-2">Верно</th>
                <th className="py-2 px-2">Ошибки (замена цвета)</th>
                <th className="py-2 px-2">из них «прочитал слово»</th>
                <th className="py-2 px-2">Не услышано</th>
                <th className="py-2 px-2">Лишние слова</th>
                <th className="py-2 px-2">Не дошёл</th>
              </tr>
            </thead>
            <tbody>
              {errorRows.map(({ p, i }) => (
                <tr key={i} className="border-b border-border">
                  <td className="py-2 px-2 text-primary font-medium">{CARD_TITLES[i]}</td>
                  <td className="py-2 px-2">{p.ok}</td>
                  <td className="py-2 px-2 font-semibold">{p.sub}</td>
                  <td className="py-2 px-2">{i === 2 ? (p.intrusions == null ? 'нет данных' : p.intrusions) : '—'}</td>
                  <td className="py-2 px-2">{p.del}</td>
                  <td className="py-2 px-2">{p.ins}</td>
                  <td className="py-2 px-2">{p.unreached}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {confusions.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-primary mb-1">Что называл вместо нужного цвета</p>
            <div className="flex flex-wrap gap-2">
              {confusions.map((c, k) => (
                <span key={k} className="text-[11px] bg-background border border-border px-2 py-1 rounded-full">
                  {c.card}: {c.exp} → {c.said} ×{c.n}
                </span>
              ))}
            </div>
          </div>
        )}
        {runs.length > 0 && (
          <p className="text-xs text-muted-foreground mt-3">
            Серии одинаковых неверных ответов подряд: {runs.map(x => `${x.card}, с позиции ${x.start}: «${x.said}» ×${x.len}`).join('; ')}.
          </p>
        )}
        <p className="text-[10px] text-muted-foreground/70 mt-3 italic">
          «Прочитал слово» — ответ совпал с напечатанным словом вместо цвета чернил (классическая интерференционная ошибка). Самопоправки пациента распознаются как отдельные слова (см. «Лишние слова»).
        </p>
      </Card>

      {/* 4. Начало ответа (экспериментально) */}
      <Card
        title="Начало ответа (экспериментально)"
        note="Время до первого распознанного слова включает запуск распознавания (около 3 с у любого пациента), поэтому абсолютное значение не показатель. Осмысленно только сравнение карточек. Паузы и темп внутри карточки не показываются: браузерное распознавание отдаёт слова крупными порциями, и по ним нельзя отличить паузу пациента от особенности потока."
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {per.map((p, i) => (
            <Tile key={i} label={`Первое слово, ${CARD_LETTERS[i]}`} value={p.latencyS != null ? `${num(p.latencyS, 1)} с` : '—'} />
          ))}
          <Tile label="C − B" value={latDiff != null ? `${signed(latDiff, 1)} с` : '—'} sub="разница начала ответа" />
        </div>
      </Card>

      {/* 5. Наблюдения */}
      <Card title="Наблюдения для врача" note="Не диагноз и не заключение: автоматические отметки по данным записи. Нормативов для этой версии теста нет, поэтому выраженность не оценивается.">
        {obs.length > 0 ? (
          <ul className="list-disc pl-5 text-sm space-y-1.5">
            {obs.map(t => <li key={t}>{t}</li>)}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Отдельных отметок нет.</p>
        )}
        {norms && norms.status !== 'set' && (
          <p className="flex items-start gap-2 text-xs text-muted-foreground mt-3">
            <Info className="size-4 shrink-0 mt-0.5" />
            Для оценки выраженности нужно сопоставление с нормой по возрасту. Для этой версии теста нормы пока не заданы (набор локальных контролей).
          </p>
        )}
      </Card>

      {/* 6. Таблица показателей */}
      <Card
        title="Показатели"
        note="Индекс скорости — расчётная величина (правильных слов за 45 с при данном темпе), не балл со шкалой и не норма Golden. T-балл не рассчитывается без нормативных таблиц."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b-2 border-border text-muted-foreground text-xs">
                <th className="py-2 px-2">Субтест</th>
                <th className="py-2 px-2">Время, с</th>
                <th className="py-2 px-2">Метод</th>
                <th className="py-2 px-2">с/слово</th>
                <th className="py-2 px-2">Покрытие</th>
                <th className="py-2 px-2">Индекс скорости (45 с)</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(([p, i]) => (
                <tr key={i} className="border-b border-border">
                  <td className="py-2 px-2 text-primary font-medium">{CARD_TITLES[i]}</td>
                  <td className="py-2 px-2">{p.span}</td>
                  <td className="py-2 px-2 text-[10px] text-muted-foreground">{SOURCE_TEXT[p.source] || p.source}</td>
                  <td className="py-2 px-2">{p.perItemS != null ? num(p.perItemS, 2) : (p.perItemOwnS != null ? `${num(p.perItemOwnS, 2)}*` : '—')}</td>
                  <td className="py-2 px-2">{p.coverage != null ? `${Math.round(p.coverage * 100)}%` : '—'}</td>
                  <td className="py-2 px-2 font-semibold text-accent">{s.golden ? [s.golden.W, s.golden.C, s.golden.CW][i] ?? '—' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-muted-foreground/70 mt-2 italic">
          * справочно: по собственному источнику времени карточки, в сопоставление не входит.
        </p>
      </Card>

      {/* 7. Паспорт измерения и ограничения */}
      <div className="bg-background border border-border rounded-3xl p-6">
        <div className="flex items-center gap-2 mb-3">
          <Info className="size-5 text-primary" />
          <h2 className="text-lg font-semibold text-primary">Паспорт измерения и ограничения</h2>
        </div>
        <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1.5">
          <li>Голосовая версия теста Струпа: 4 цвета, карточки 100 / 50 / 50 слов. Мера — время выполнения карточки и ошибки; это не бумажный тест Golden, его нормы неприменимы.</li>
          <li>Время речи определяется по распознаванию в браузере (Chrome, Web Speech): задержка ~0,5–1,5 с входит в измерения и частично сокращается в отношении C/B.</li>
          <li>Нормативы для этой версии: {norms && norms.status === 'set' ? `заданы (${norms.band}${norms.source ? `, ${norms.source}` : ''})` : 'не заданы — собираются локальные контроли (три возрастные группы, один протокол).'} Вердиктов «норма / нарушение» отчёт не выносит.</li>
          <li>Потеря слов распознаванием и пропуск пациента без аудиозаписи не различаются.</li>
          <li>На результат влияют цветовое зрение, коррекция зрения, дизартрия, утомление, седативные препараты, глубина депрессии и общая замедленность; автоматически они не учитываются.</li>
          <li>Черновик для врача, не диагноз.</li>
        </ul>
        {pilot && (
          <div className="flex flex-wrap gap-2 mt-4">
            {Object.entries(PILOT_LABELS).map(([key, [label, values]]) => (
              <span key={key} className="text-[11px] bg-secondary px-2 py-1 rounded-full">
                {label}: {values[pilot[key]] || 'не указано'}
              </span>
            ))}
            {pilot.nativeLang && <span className="text-[11px] bg-secondary px-2 py-1 rounded-full">Родной язык: {pilot.nativeLang}</span>}
            {pilot.engine && <span className="text-[11px] bg-secondary px-2 py-1 rounded-full">Распознавание: {pilot.engine}</span>}
          </div>
        )}
      </div>

      {/* 8. Примечания врача — поле для рукописных заметок при печати */}
      <div className="bg-background border border-border rounded-3xl p-6">
        <h2 className="text-lg font-semibold text-primary mb-2">Примечания врача</h2>
        <div className="border border-dashed border-border rounded-2xl min-h-[96px]" />
      </div>
    </>
  );
}

// Форма прохождения приходит в данных теста (raw.form: 'oral' | 'written').
// Устная и письменная формы — разные измерения: нормы и пороги у них раздельные.
// Старые записи без поля form показываются как «форма не указана», а не как устные.
const SDMT_FORM_LABEL = {
  oral: 'устная форма',
  written: 'ручной ввод (письменная форма)',
};

function SdmtBody({ r }) {
  const { raw, band } = r;
  // Форма: из данных теста, а если их нет — из ответа сервера (r.norms.form).
  const form = SDMT_FORM_LABEL[raw?.form] ? raw.form : (SDMT_FORM_LABEL[r.norms?.form] ? r.norms.form : 'unknown');
  const formLabel = SDMT_FORM_LABEL[form] || 'форма не указана';
  // Блок норм приходит с сервера (score_result.norms, sdmt-web-2). Путь, по которому
  // он попадает в report, зависит от сборщика отчёта: пока не подключён, блок
  // честно говорит, что норм нет.
  const norms = r.norms || r.scoreResult?.norms || null;
  const normsSet = norms?.status === 'set';
  return (
    <>
      <div className={`rounded-3xl p-6 shadow-xl border-2 flex items-center justify-between ${BAND_STYLE[band.cls]}`}>
        <div>
          <p className="text-sm font-medium">Сырой балл ({formLabel})</p>
          <p className="text-5xl font-bold">{raw.score}</p>
        </div>
        <p className="text-lg font-semibold text-right max-w-[240px]">{band.label}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Metric label="Ошибки" value={raw.errors} />
        <Metric label="Пропуски" value={raw.omissions} />
        <Metric label="Динамика (30с)" value={raw.intervals ? raw.intervals.join(' · ') : '—'} />
      </div>

      <div className="bg-secondary/50 rounded-3xl p-6 shadow-xl border border-border">
        <h2 className="text-lg font-semibold text-primary mb-2">Форма прохождения</h2>
        <p className="text-sm font-semibold text-accent">{formLabel}</p>
        <p className="text-xs text-muted-foreground mt-2">
          {normsSet
            ? `${norms.provisional ? 'Предварительный ориентир, не норма (исправить после пилота)' : 'Сравнение с нормой для этой формы'} (${norms.ageBand} лет): z = ${norms.z > 0 ? '+' : ''}${norms.z}.${norms.source ? ` Источник: ${norms.source}.` : ''}`
            : form === 'unknown'
              ? 'В записи нет отметки о форме, поэтому сопоставить результат с нормой нельзя.'
              : `Нормы для формы «${formLabel}» не заданы: вердикта «норма / снижение» отчёт не выносит.`}
        </p>
        <p className="text-[10px] text-muted-foreground/70 mt-2 italic">
          Устная форма и ручной ввод — разные измерения, нормы и пороги для них раздельные. Результаты разных форм между собой не сравниваются, в том числе в динамике.
        </p>
      </div>

      <div className="bg-secondary/50 rounded-3xl p-6 shadow-xl border border-border">
        <h2 className="text-lg font-semibold text-primary mb-2">Динамика при повторном тестировании</h2>
        <p className="text-xs text-muted-foreground">
          {form === 'oral'
            ? 'Минимально значимое изменение (RCI) — Δ ≥ 4 балла (ориентир для устной формы). '
            : 'Минимально значимое изменение для этой формы не определено. '}
          При повторном визите используйте
          {r.scaleId === 'sdmt_v2' ? ' вариант 1' : ' вариант 2'} — контроль эффекта практики.
          Сравнивайте только результаты одной и той же формы.
        </p>
      </div>
    </>
  );
}

export default function SpeedTestReport({ report, onExit }) {
  if (!report) return null;
  const isStroop = report.scaleId === 'stroop';
  return (
    <div className="min-h-screen bg-background p-8 overflow-y-auto">
      <div className="max-w-4xl mx-auto flex flex-col gap-6">

        <div className="bg-secondary/50 rounded-3xl p-8 shadow-xl border border-border">
          <h1 className="text-3xl font-bold text-primary">{isStroop ? 'Выписка: Тест Струпа (голосовая версия)' : report.scaleName}</h1>
          <p className="text-muted-foreground mt-1">Пациент: {report.patientName} | Дата: {report.date}</p>
          <p className="text-[10px] text-muted-foreground/60 mt-1 italic">{report.algorithm}</p>
          <p className={`text-xs font-semibold mt-2 ${report.saved ? 'text-green-700' : 'text-orange-700'}`}>
          {report.saved ? 'Отчёт сохранён в карточке пациента' : 'Результат НЕ сохранён — предварительный расчёт (проверьте, что выбран пациент со связью)'}
          </p>
        </div>

        {isStroop ? <StroopBody r={report} /> : <SdmtBody r={report} />}

        {!isStroop && (
          <div className="bg-background border border-border rounded-3xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="size-5 text-yellow-500" />
              <h2 className="text-lg font-semibold text-primary">Ограничения интерпретации</h2>
            </div>
            <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1.5">
              {report.raw?.form !== 'written' && (
                <li>Голосовой формат: задержка распознавания (~0,5–1,5 с) входит в измерения.</li>
              )}
              {report.raw?.form === 'written' && (
                <li>Ручной ввод (клики или клавиатура): в время входит моторный компонент; нормы устной формы к нему не применяются.</li>
              )}
              <li>Нормативные таблицы не подключены, T-баллы не рассчитываются. Пороги — исследовательские, до набора локальных норм.</li>
              {report.raw?.form !== 'written' && (
                <li>Самопоправки пациента распознаются как отдельные слова.</li>
              )}
              <li>Черновик для врача, не диагноз.</li>
            </ul>
          </div>
        )}

        <div className="flex justify-center pb-8">
          <button onClick={onExit} className="flex items-center gap-3 bg-primary text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-primary/90 transition-colors">
            <Home className="size-6" /> Завершить и вернуться
          </button>
        </div>
      </div>
    </div>
  );
}